/**
 * 活跃会话的唯一持久化入口。
 *
 * 一对 access/refresh 必须作为同一个 JSON 一次写入。分两个 key 写会在
 * 跨标签 `storage` 事件里出现「只写了一半」的中间态：其它标签页可能把
 * 刚登录的会话当成登出清掉，或在刷新时拿到「新 access + 旧 refresh」去
 * 重放一次性 refresh token（2026-08-30 闪退、2026-08-31 登录被踢）。
 *
 * 旧版 `accessToken` / `refreshToken` / `userInfo` 仅在读取时迁移一次，
 * 写入后立即删除，避免两套存储并存。
 */

import type { User } from '@/types'
import { parseStoredJson, readStorage, removeStorage, writeStorage } from '@/utils/storage'

export const AUTH_SESSION_KEY = 'authSession'
export const LEGACY_ACCESS_TOKEN_KEY = 'accessToken'
export const LEGACY_REFRESH_TOKEN_KEY = 'refreshToken'
export const LEGACY_USER_INFO_KEY = 'userInfo'

export type AuthSessionSnapshot = {
  accessToken: string
  refreshToken: string
  user: User | null
}

type PersistedAuthSessionV1 = {
  v: 1
  accessToken: string
  refreshToken: string
  user: User | null
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function toUser(value: unknown): User | null {
  if (!value || typeof value !== 'object') {
    return null
  }
  return value as User
}

export function parseAuthSessionJson(raw: string | null | undefined): AuthSessionSnapshot | null {
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PersistedAuthSessionV1>
    if (!isNonEmptyString(parsed.accessToken) || !isNonEmptyString(parsed.refreshToken)) {
      return null
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      user: toUser(parsed.user)
    }
  } catch {
    return null
  }
}

export function serializeAuthSession(snapshot: AuthSessionSnapshot): string {
  const payload: PersistedAuthSessionV1 = {
    v: 1,
    accessToken: snapshot.accessToken,
    refreshToken: snapshot.refreshToken,
    user: snapshot.user
  }
  return JSON.stringify(payload)
}

export function readAuthSession(): AuthSessionSnapshot | null {
  return parseAuthSessionJson(readStorage(AUTH_SESSION_KEY))
}

export function removeLegacyAuthKeys(): void {
  removeStorage(LEGACY_ACCESS_TOKEN_KEY)
  removeStorage(LEGACY_REFRESH_TOKEN_KEY)
  removeStorage(LEGACY_USER_INFO_KEY)
}

export function writeAuthSession(snapshot: AuthSessionSnapshot | null): void {
  if (!snapshot || !snapshot.accessToken || !snapshot.refreshToken) {
    removeStorage(AUTH_SESSION_KEY)
    removeLegacyAuthKeys()
    return
  }

  writeStorage(
    AUTH_SESSION_KEY,
    serializeAuthSession({
      accessToken: snapshot.accessToken,
      refreshToken: snapshot.refreshToken,
      user: snapshot.user
    })
  )
  removeLegacyAuthKeys()
}

export function readLegacyAuthSession(): AuthSessionSnapshot | null {
  const accessToken = readStorage(LEGACY_ACCESS_TOKEN_KEY)
  const refreshToken = readStorage(LEGACY_REFRESH_TOKEN_KEY)
  if (!accessToken || !refreshToken) {
    return null
  }
  return {
    accessToken,
    refreshToken,
    user: parseStoredJson<User>(LEGACY_USER_INFO_KEY)
  }
}

/**
 * 启动时读取活跃会话：优先规范键；否则把旧版三键迁过来并删掉旧键。
 * 残缺的旧键（只剩其中一个）一律清掉，避免下次再被当成有效会话。
 */
export function restoreAuthSession(): AuthSessionSnapshot | null {
  const current = readAuthSession()
  if (current) {
    removeLegacyAuthKeys()
    return current
  }

  const legacy = readLegacyAuthSession()
  if (!legacy) {
    removeLegacyAuthKeys()
    return null
  }

  writeAuthSession(legacy)
  return legacy
}
